import { createContext, PropsWithChildren, useContext } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { AuthContextType, useAuthContext } from './useAuthContext';
import { usePostHogIdentify } from '@/integrations/posthog/usePostHogIdentify';

const AuthContext = createContext<AuthContextType | null>(null);

const loadingContainerStyles = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  minHeight: '100vh',
} as const;

const loadingInnerStyles = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 2,
} as const;

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }

  return context;
};

export const AuthProvider = ({ children }: PropsWithChildren) => {
  const auth = useAuthContext();
  usePostHogIdentify(auth.sessionUser);

  const { isLoading } = auth;

  if (isLoading) {
    return (
      <Box sx={loadingContainerStyles}>
        <Box sx={loadingInnerStyles}>
          <CircularProgress color="primary" size={32} />
          <Typography>Loading...</Typography>
        </Box>
      </Box>
    );
  }

  return <AuthContext.Provider value={auth}>{children}</AuthContext.Provider>;
};
